import React, { useEffect, useState } from "react";
import PopupDialog from "components/dialog/PopupDialog";
import PhotoDropUpload from "components/form-elements/PhotoDropUpload";
import { useAuth } from "context/AuthProvider";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { OrganizationDetail } from "./OrganizationManagementPage";

export default function OrganizationProfilePage() {
  const { user, replaceUser } = useAuth();
  const [showMsg] = useMsgBox();
  const [, , savingPhoto, axiosSavePhoto] = useAxiosFunction();
  const [, , savingContact, axiosSaveContact] = useAxiosFunction();
  const [photoPopup, setPhotoPopup] = useState(false);
  const [contactPopup, setContactPopup] = useState(false);
  const [draftLogo, setDraftLogo] = useState("");
  const [contactForm, setContactForm] = useState({
    alamat: "",
    telepon: "",
    email: "",
    website: "",
  });
  const [profileResp, profileError, profileLoading, refetchProfile] = useAxios({
    method: "GET",
    url: "/profile/organisasi",
    auth: true,
  });

  const item = profileResp?.data || null;

  useEffect(() => {
    if (!item) return;
    setDraftLogo(item?.logo || "");
    setContactForm({
      alamat: item?.alamat || "",
      telepon: item?.telepon || "",
      email: item?.email || "",
      website: item?.website || "",
    });
  }, [item]);

  const openPhotoPopup = () => {
    setDraftLogo(item?.logo || "");
    setPhotoPopup(true);
  };

  const openContactPopup = () => {
    setContactForm({
      alamat: item?.alamat || "",
      telepon: item?.telepon || "",
      email: item?.email || "",
      website: item?.website || "",
    });
    setContactPopup(true);
  };

  const savePhoto = async () => {
    if (!draftLogo) {
      await showMsg("Informasi", "Silakan upload logo terlebih dahulu.");
      return;
    }
    const hasil = await axiosSavePhoto({
      method: "PUT",
      url: "/profile/organisasi/photo",
      auth: true,
      data: { logo: draftLogo },
    });
    if (hasil?.data?.success === true) {
      await showMsg("Informasi", "Logo organisasi berhasil disimpan.");
      await replaceUser({ ...user, photo: draftLogo, photo_location: "organisasi-logo" });
      setPhotoPopup(false);
      if (typeof refetchProfile === "function") {
        refetchProfile();
      }
      return;
    }
    await showMsg("Error", hasil?.message || "Logo organisasi gagal disimpan.");
  };

  const saveContact = async () => {
    if (!contactForm.alamat || !contactForm.telepon || !contactForm.email) {
      await showMsg("Informasi", "Alamat, telepon, dan email wajib diisi.");
      return;
    }
    const hasil = await axiosSaveContact({
      method: "PUT",
      url: "/profile/organisasi/contact",
      auth: true,
      data: contactForm,
    });
    if (hasil?.data?.success === true) {
      await showMsg("Informasi", "Profil organisasi berhasil disimpan.");
      await replaceUser({ ...user, email: contactForm.email, username: contactForm.email });
      setContactPopup(false);
      if (typeof refetchProfile === "function") {
        refetchProfile();
      }
      return;
    }
    await showMsg("Error", hasil?.message || "Profil organisasi gagal disimpan.");
  };

  const updateContactForm = (field, value) => {
    setContactForm((current) => ({ ...current, [field]: value }));
  };

  if (profileLoading) {
    return <div>Memuat profil organisasi...</div>;
  }

  if (profileError) {
    return <div className="alert alert-danger mb-0">Profil organisasi belum dapat dimuat.</div>;
  }

  if (!item) {
    return <div className="text-muted">Profil organisasi belum tersedia.</div>;
  }

  return (
    <>
      <OrganizationDetail
        item={item}
        headerActions={
          <>
            <button type="button" className="btn btn-sm btn-outline-primary mr-2" onClick={openPhotoPopup}>
              <i className="fas fa-camera mr-1" />
              Edit Logo
            </button>
            <button type="button" className="btn btn-sm btn-outline-success" onClick={openContactPopup}>
              <i className="fas fa-edit mr-1" />
              Edit
            </button>
          </>
        }
      />

      <PopupDialog
        title="Edit Logo Organisasi"
        size="md"
        show={photoPopup}
        closeHandle={() => setPhotoPopup(false)}
        doHandle={savePhoto}
        doLabel={savingPhoto ? "Menyimpan..." : "Simpan"}
        doDisabled={savingPhoto || !draftLogo}
      >
        <PhotoDropUpload
          name="logo"
          dropname="logo"
          url="/file/upload"
          auth
          data={{ name: "logo", location: "organisasi-logo" }}
          value={draftLogo}
          setValue={(name, value) => setDraftLogo(value)}
          onSuccess={(filename) => setDraftLogo(filename)}
          previewWidth={180}
          previewHeight={180}
          caption="Upload logo baru, lalu klik Simpan."
        />
      </PopupDialog>

      <PopupDialog
        title="Edit Profil Organisasi"
        size="lg"
        show={contactPopup}
        closeHandle={() => setContactPopup(false)}
        doHandle={saveContact}
        doLabel={savingContact ? "Menyimpan..." : "Simpan"}
        doDisabled={savingContact}
      >
        <div className="row">
          <div className="col-md-12 form-group">
            <label>Alamat <span className="text-danger">*</span></label>
            <textarea
              className="form-control"
              rows={3}
              value={contactForm.alamat}
              onChange={(event) => updateContactForm("alamat", event.target.value)}
            />
          </div>
          <div className="col-md-4 form-group">
            <label>Telepon <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              value={contactForm.telepon}
              onChange={(event) => updateContactForm("telepon", event.target.value)}
            />
          </div>
          <div className="col-md-4 form-group">
            <label>Email <span className="text-danger">*</span></label>
            <input
              type="email"
              className="form-control"
              value={contactForm.email}
              onChange={(event) => updateContactForm("email", event.target.value)}
            />
          </div>
          <div className="col-md-4 form-group">
            <label>Website</label>
            <input
              type="text"
              className="form-control"
              value={contactForm.website}
              onChange={(event) => updateContactForm("website", event.target.value)}
            />
          </div>
        </div>
      </PopupDialog>
    </>
  );
}
